/**
 * Submission Actions Hook
 * Admin actions for managing contact submissions: reply, update status
 */

import { apiUrl } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { useCallback, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

export interface ActionResult {
  success: boolean;
  error: string | null;
}

export interface UseSubmissionActionsResult {
  replyToSubmission: (submissionId: number, reply: string) => Promise<ActionResult>;
  updateStatus: (
    submissionId: number,
    status: 'new' | 'in_progress' | 'resolved',
  ) => Promise<ActionResult>;
  loading: boolean;
}

// =============================================================================
// Hook
// =============================================================================

export function useSubmissionActions(): UseSubmissionActionsResult {
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(false);

  const replyToSubmission = useCallback(
    async (submissionId: number, reply: string): Promise<ActionResult> => {
      setLoading(true);
      try {
        const response = await authFetch(
          apiUrl(`/api/admin/contact/submissions/${submissionId}/reply`),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reply }),
          },
        );

        if (!response.ok) {
          const data = await response.json();
          return {
            success: false,
            error: data.error || 'Failed to send reply',
          };
        }

        return { success: true, error: null };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Unknown error',
        };
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  const updateStatus = useCallback(
    async (
      submissionId: number,
      status: 'new' | 'in_progress' | 'resolved',
    ): Promise<ActionResult> => {
      setLoading(true);
      try {
        const response = await authFetch(
          apiUrl(`/api/admin/contact/submissions/${submissionId}/status`),
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status }),
          },
        );

        if (!response.ok) {
          const data = await response.json();
          return {
            success: false,
            error: data.error || 'Failed to update status',
          };
        }

        return { success: true, error: null };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Unknown error',
        };
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  return {
    replyToSubmission,
    updateStatus,
    loading,
  };
}
